import React, { useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDocument } from 'react-firebase-hooks/firestore';
import { firestore } from './firebase';
import { StimmexText } from './Types';
import stimmexLogo from './img/logo_anim.gif';

type FormState = {
  name: string;
  email: string;
  occupation: string;
};

const initialFormState: FormState = {
  name: '',
  email: '',
  occupation: '',
};

const OpenLetterSign: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const history = useHistory();
  const [form, setForm] = useState<FormState>(initialFormState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const introDocRef = React.useMemo(() => firestore.collection('texte').doc('openLetterIntro'), []);
  const motivationDocRef = React.useMemo(() => firestore.collection('texte').doc('openLetterMotivation'), []);
  const warumHandelnDocRef = React.useMemo(() => firestore.collection('texte').doc('openLetterWarumHandeln'), []);
  const worumDocRef = React.useMemo(() => firestore.collection('texte').doc('openLetterWorum'), []);
  const letterDocRef = React.useMemo(() => firestore.collection('texte').doc('openLetterText'), []);

  const [introSnapshot, introLoading, introError] = useDocument(introDocRef);
  const [motivationSnapshot, motivationLoading, motivationError] = useDocument(motivationDocRef);
  const [warumHandelnSnapshot, warumHandelnLoading, warumHandelnError] = useDocument(warumHandelnDocRef);
  const [worumSnapshot, worumLoading, worumError] = useDocument(worumDocRef);
  const [letterSnapshot, letterLoading, letterError] = useDocument(letterDocRef);

  const docsLoading =
    introLoading || motivationLoading || warumHandelnLoading || worumLoading || letterLoading;
  const docsError = introError || motivationError || warumHandelnError || worumError || letterError;

  const defaultMotivation =
    'Wir sammeln Unterschriften, um den offenen Brief an verantwortliche Stellen weiterzugeben. Jede Stimme hilft, Aufmerksamkeit zu schaffen.';
  const defaultLetterContent =
    'Sehr geehrte Damen und Herren,\n\nwir bitten um Ihre Unterstützung für dieses Anliegen. Bitte lesen Sie den offenen Brief sorgfältig durch und unterschreiben Sie, wenn Sie die Forderungen mittragen.';

  const introData = introSnapshot?.data() as StimmexText | undefined;
  const motivationData = motivationSnapshot?.data() as StimmexText | undefined;
  const warumHandelnData = warumHandelnSnapshot?.data() as StimmexText | undefined;
  const worumData = worumSnapshot?.data() as StimmexText | undefined;
  const letterData = letterSnapshot?.data() as StimmexText | undefined;

  const pageTitle = introData?.title ?? 'Open Letter #MITTELBÜHNE UNVERZICHTBAR';
  const introContent = introData?.content ?? '';
  const motivationTitle = motivationData?.title ?? letterData?.title ?? 'Offener Brief';
  const motivationContent = motivationData?.content ?? defaultMotivation;
  const motivationIndex = motivationData?.index ?? 0;
  const warumHandelnTitle = warumHandelnData?.title ?? 'Warum handeln?';
  const warumHandelnContent = warumHandelnData?.content ?? '';
  const warumHandelnIndex = warumHandelnData?.index ?? 1;
  const worumTitle = worumData?.title ?? 'Worum geht es?';
  const worumContent = worumData?.content ?? '';
  const worumIndex = worumData?.index ?? 2;
  const letterTitle = letterData?.title ?? 'Offener Brief';
  const letterContent = letterData?.content ?? defaultLetterContent;
  const letterIndex = letterData?.index ?? 3;

  const accordionSections = React.useMemo(
    () =>
      [
        { id: 'motivation', title: motivationTitle, content: motivationContent, index: motivationIndex },
        { id: 'warumHandeln', title: warumHandelnTitle, content: warumHandelnContent, index: warumHandelnIndex },
        { id: 'worum', title: worumTitle, content: worumContent, index: worumIndex },
        { id: 'letter', title: letterTitle, content: letterContent, index: letterIndex },
      ]
        .filter((section) => section.content && section.content.trim().length > 0)
        .sort((a, b) => a.index - b.index),
    [
      motivationTitle,
      motivationContent,
      motivationIndex,
      warumHandelnTitle,
      warumHandelnContent,
      warumHandelnIndex,
      worumTitle,
      worumContent,
      worumIndex,
      letterTitle,
      letterContent,
      letterIndex,
    ]
  );

  useEffect(() => {
    if (docsLoading) {
      return;
    }

    if (openSection && !accordionSections.some((section) => section.id === openSection)) {
      setOpenSection(null);
    }
  }, [accordionSections, docsLoading, openSection]);

  const asHtml = (content: string) =>
    /<[a-z][\s\S]*>/i.test(content) ? content : content.replace(/\n/g, '<br />');

  const handleSectionToggle = (sectionId: string) => {
    setOpenSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const handleScrollToForm = () => {
    window.requestAnimationFrame(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => {
          formRef.current?.focus({ preventScroll: true });
        }, 500);
      }
    });
  };

  useEffect(() => {
    const originalHtmlTouch = document.documentElement.style.touchAction;
    const originalBodyTouch = document.body.style.touchAction;
    const originalBodyOverflow = document.body.style.overflow;

    document.documentElement.style.touchAction = 'auto';
    document.body.style.touchAction = 'auto';
    document.body.style.overflow = originalBodyOverflow || 'auto';

    return () => {
      document.documentElement.style.touchAction = originalHtmlTouch;
      document.body.style.touchAction = originalBodyTouch;
      document.body.style.overflow = originalBodyOverflow;
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const docId = `OpenLetterSignature-${new Date().toISOString()}`;
    const subject = `Neue Unterschrift: ${motivationTitle}`;

    firestore
      .collection('/mail')
      .doc(docId)
      .set({
        to: 'kontakt@stimmex.de',
        replyTo: form.email,
        message: {
          subject,
          html: `
            <table>
              <tr>
                <td>Name</td>
                <td>${form.name}</td>
              </tr>
              <tr>
                <td>E-Mail</td>
                <td>${form.email}</td>
              </tr>
              <tr>
                <td>Beruf</td>
                <td>${form.occupation}</td>
              </tr>
              <tr>
                <td>Open-Letter-UUID</td>
                <td>${uuid ?? 'unbekannt'}</td>
              </tr>
            </table>
          `,
        },
      })
      .then(() => {
        setSubmitted(true);
        setForm(initialFormState);
        setRedirectCountdown(15);
      })
      .catch(() => {
        setError('Das Absenden ist leider fehlgeschlagen. Bitte versuche es erneut.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    if (redirectCountdown === null) {
      return;
    }

    if (redirectCountdown === 0) {
      history.push('/');
      return;
    }

    const timeout = window.setTimeout(() => {
      setRedirectCountdown((prev) => (prev !== null ? prev - 1 : prev));
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [redirectCountdown, history]);

  if (docsLoading) {
    return (
      <div className="open-letter-layout">
        <div className="open-letter-card">Lädt...</div>
      </div>
    );
  }

  if (docsError) {
    return (
      <div className="open-letter-layout">
        <div className="open-letter-card">Der offene Brief konnte nicht geladen werden.</div>
      </div>
    );
  }

  const htmlFrom = (markup: string) => ({ __html: markup });

  return (
    <div className="open-letter-layout">
      <div className="open-letter-card">
        <header className="open-letter-header">
          <button className="open-letter-logo" onClick={() => history.push('/')}
            aria-label="Zur Startseite">
            <img src={stimmexLogo} alt="Stimme X" />
          </button>
        </header>
        <h1 className="open-letter-title">{pageTitle}</h1>

        {introContent && (
          <div
            className="open-letter-intro"
            dangerouslySetInnerHTML={htmlFrom(asHtml(introContent))}
          />
        )}

        <div className="open-letter-intro-cta">
          <button type="button" className="open-letter-cta-button" onClick={handleScrollToForm}>
            Jetzt unterschreiben!
          </button>
        </div>

        <div className="open-letter-accordion" aria-live="polite">
          {accordionSections.length === 0 ? (
            <div className="open-letter-accordion-empty">Die Inhalte sind derzeit nicht verfügbar.</div>
          ) : (
            accordionSections.map((section, index) => {
              const isOpen = openSection === section.id;
              const isFirst = index === 0;
              return (
                <div
                  key={section.id}
                  className={`open-letter-accordion-item ${isFirst ? 'open-letter-accordion-item-first' : ''}`}
                >
                  <button
                    type="button"
                    className="open-letter-accordion-trigger"
                    onClick={() => handleSectionToggle(section.id)}
                    aria-expanded={isOpen}
                    aria-controls={`open-letter-panel-${section.id}`}
                  >
                    <span className="open-letter-accordion-title">{section.title}</span>
                    <span className="open-letter-accordion-icon" aria-hidden="true">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                  <div
                    id={`open-letter-panel-${section.id}`}
                    className="open-letter-panel"
                    hidden={!isOpen}
                  >
                    <div
                      className="open-letter-panel-text"
                      dangerouslySetInnerHTML={htmlFrom(asHtml(section.content))}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        <hr className="open-letter-divider" />

        <section>
          <h2 className="open-letter-subtitle">Unterzeichnen</h2>
          {submitted ? (
            <div className="open-letter-success">
              <p>Vielen Dank für Ihre Unterstützung!</p>
              {redirectCountdown !== null && redirectCountdown > 0 && (
                <p className="open-letter-countdown">
                  Du wirst in {redirectCountdown} Sekunden zur Startseite weitergeleitet.
                </p>
              )}
            </div>
          ) : (
            <form className="open-letter-form" onSubmit={handleSubmit} ref={formRef} tabIndex={-1}>
              {error && <div className="open-letter-error">{error}</div>}
              <label className="open-letter-label" htmlFor="name">
                Vollständiger Name
              </label>
              <input
                className="open-letter-input"
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                required
              />

              <label className="open-letter-label" htmlFor="email">
                E-Mail-Adresse
              </label>
              <input
                className="open-letter-input"
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
              />

              <label className="open-letter-label" htmlFor="occupation">
                Tätigkeit / Beruf
              </label>
              <input
                className="open-letter-input"
                id="occupation"
                name="occupation"
                type="text"
                value={form.occupation}
                onChange={handleChange}
                required
              />

              <button className="open-letter-submit" type="submit" disabled={submitting}>
                {submitting ? 'Sendet...' : 'Jetzt unterschreiben'}
              </button>
            </form>
          )}
        </section>
      </div>

      <style>{`
        .open-letter-layout {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 16px;
          background: linear-gradient(180deg, #f9fafb 0%, #eef2f7 100%);
          overflow-y: auto;
          width: 100%;
        }

        .open-letter-card {
          width: 100%;
          max-width: 720px;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 12px 30px rgba(18, 38, 63, 0.12);
          padding: 32px;
          max-height: calc(100vh - 64px);
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .open-letter-header {
          display: flex;
          justify-content: flex-start;
          margin-bottom: 24px;
        }

        .open-letter-logo {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
        }

        .open-letter-logo img {
          width: 140px;
          height: auto;
        }

        .open-letter-title {
          font-size: 2rem;
          margin-bottom: 16px;
        }

        .open-letter-intro {
          margin-bottom: 20px;
          color: #2d3748;
          line-height: 1.6;
          padding: 0 18px;
        }

        .open-letter-intro p {
          margin-bottom: 16px;
        }

        .open-letter-intro > :last-child {
          margin-bottom: 0;
        }

        .open-letter-intro-cta {
          display: flex;
          justify-content: flex-start;
          margin-bottom: 24px;
        }

        .open-letter-accordion {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
        }

        .open-letter-accordion-item {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #ffffff;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
        }

        .open-letter-accordion-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 18px;
          border: none;
          background: transparent;
          font-size: 1rem;
          font-weight: 500;
          color: #1a202c;
          cursor: pointer;
        }

        .open-letter-accordion-trigger:hover,
        .open-letter-accordion-trigger:focus {
          background: rgba(212, 52, 64, 0.08);
          outline: none;
        }

        .open-letter-accordion-title {
          font-weight: 700;
          font-size: 1.05rem;
          text-align: left;
          flex: 1 1 auto;
          margin-right: 16px;
          word-break: break-word;
        }

        .open-letter-accordion-icon {
          font-size: 1.25rem;
          line-height: 1;
          color: #d43440;
          flex: 0 0 auto;
          margin-left: auto;
        }

        .open-letter-panel {
          padding: 0 18px 18px;
        }

        .open-letter-panel-text p {
          margin-bottom: 16px;
          line-height: 1.6;
          color: #2d3748;
        }

        .open-letter-panel-text > :last-child {
          margin-bottom: 0;
        }

        .open-letter-accordion-empty {
          padding: 16px;
          border-radius: 12px;
          border: 1px dashed #cbd5e0;
          color: #4a5568;
          text-align: center;
          background: rgba(226, 232, 240, 0.3);
        }

        .open-letter-cta-button {
          padding: 12px 18px;
          border-radius: 999px;
          border: none;
          background: #d43440;
          color: #ffffff;
          font-weight: 700;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .open-letter-cta-button:hover,
        .open-letter-cta-button:focus {
          transform: translateY(-1px);
          box-shadow: 0 10px 20px rgba(212, 52, 64, 0.25);
          outline: none;
        }

        .open-letter-divider {
          margin: 32px 0;
          border: none;
          height: 1px;
          background: #e2e8f0;
        }

        .open-letter-subtitle {
          font-size: 1.5rem;
          margin-bottom: 16px;
        }

        .open-letter-success {
          padding: 16px;
          background: #ebf8ff;
          border-radius: 8px;
          color: #2b6cb0;
        }

        .open-letter-countdown {
          margin-top: 8px;
          font-size: 0.95rem;
          color: #1a365d;
        }

        .open-letter-form {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }

        .open-letter-error {
          padding: 12px;
          border-radius: 8px;
          background: #fff5f5;
          color: #c53030;
        }

        .open-letter-label {
          font-weight: 600;
          color: #2d3748;
        }

        .open-letter-input {
          width: 100%;
          padding: 12px 14px;
          border-radius: 8px;
          border: 1px solid #cbd5e0;
          font-size: 1rem;
        }

        .open-letter-input:focus {
          outline: none;
          border-color: #d43440;
          box-shadow: 0 0 0 3px rgba(212, 52, 64, 0.3);
        }

        .open-letter-submit {
          margin-top: 8px;
          padding: 12px 16px;
          background: #d43440;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .open-letter-submit:disabled {
          cursor: not-allowed;
          opacity: 0.8;
        }

        .open-letter-submit:hover {
          background: #a1262e;
        }

        @media (max-width: 640px) {
          .open-letter-layout {
            align-items: flex-start;
            padding: 24px 12px;
          }

          .open-letter-card {
            padding: 24px;
            max-height: none;
            overflow: visible;
          }

          .open-letter-title {
            font-size: 1.75rem;
          }

          .open-letter-accordion-trigger {
            padding: 14px 16px;
            font-size: 0.95rem;
          }

          .open-letter-accordion-title {
            font-size: 1rem;
            margin-right: 12px;
          }

          .open-letter-panel {
            padding: 0 16px 16px;
          }

          .open-letter-intro {
            padding: 0 16px;
          }

          .open-letter-intro-cta {
            justify-content: center;
          }

          .open-letter-cta-button {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default OpenLetterSign;
